import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../context/AuthContext.js';
import { useAuthContext } from '../hooks/useAuthContext.js';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.js';
import News from '../components/News.js';
import Roadmap from '../components/Roadmap.js';
import { 
  FaPalette, 
  FaUsers, 
  FaBuilding, 
  FaChartLine,
  FaCode,
  FaServer,
  FaDatabase,
  FaCog,
  FaUserCog,
  FaWarehouse,
  FaMicrochip,
  FaCircle,
  FaNewspaper,
  FaRoad,
  FaBlog
} from 'react-icons/fa';

export default function Settings() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalArtworks: 0,
    totalUsers: 0,
    totalGalleries: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && !user.permissions.includes('admin')) {
      navigate('/home');
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      const [artworksRes, usersRes, galleriesRes] = await Promise.all([
        axiosInstance.get('/artworks/artworks'),
        axiosInstance.get('/user?limit=1000&page=1'),
        axiosInstance.get('/gallery?limit=1000&page=1')
      ]);

      setStats({
        totalArtworks: artworksRes.data.length || 0,
        totalUsers: usersRes.data.totalUsersCount || 0,
        totalGalleries: galleriesRes.data.totalGalleriesCount || 0,
      });
    } catch (error) {
      console.error('Error fetching stats: ', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 font-['Montserrat']">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#343E4E'}}>
          <Icon className="text-white text-xl" />
        </div>
      </div>
    </div>
  );

  const ActionCard = ({ title, description, icon: Icon, color, onClick }) => (
    <div 
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group font-['Montserrat']"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200" style={{backgroundColor: '#343E4E'}}>
          <Icon className="text-white text-lg" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-900 mb-1">
            {title}
          </h3>
          <p className="text-sm text-gray-500">
            {description}
          </p>
        </div>
      </div>
    </div>
  );

  const InfoCard = ({ label, value, icon: Icon, color, isOnline }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow duration-200 font-['Montserrat']">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#343E4E'}}>
          <Icon className="text-white text-sm" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <div className="flex items-center gap-2 mt-1">
            {isOnline && <FaCircle className="text-green-500 text-xs" />}
            <p className="text-sm font-semibold text-gray-900">{value}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Navbar />
      <div className="min-h-screen sm:ml-20 ml-0 flex flex-col flex-grow bg-gray-50 font-['Montserrat']">
        <div className="bg-white border-b border-gray-200 px-8 py-6 mb-8 sm:flex justify-between items-center flex-col sm:flex-row">
          <div className='pt-0'>
            <h1 className="sm:text-3xl text-xl font-bold text-gray-900">
              Administration
            </h1>
            <p className="text-gray-600 mt-1">Monitor platform metrics and manage system settings</p>
          </div>
          <div className="flex items-center gap-6 pt-2">
            <div className="flex items-center gap-2 px-2 sm:px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
              <FaCode className="text-gray-500 text-sm" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">Version <span className="font-bold text-gray-900">1.0.0</span></span>
            </div>
            <div className="flex items-center gap-2 px-2 sm:px-4 py-2 bg-green-50 rounded-lg border border-green-200">
              <FaCircle className="text-green-500 text-xs" />
              <span className="text-xs sm:text-sm font-medium text-green-700">Database <span className="font-bold text-green-800">Connected</span></span>
            </div>
          </div>
        </div>

        <div className="sm:px-8 px-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-600">Loading dashboard data...</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="lg:col-span-1 order-1">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Platform Overview</h2>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 min-h-[220px] flex flex-col justify-center">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 bg-transparent rounded-lg p-4 border border-gray-200 flex flex-col justify-center">
                      <div className="flex flex-row sm:flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#343E4E'}}>
                          <FaPalette className="text-white text-xl" />
                        </div>
                        <div className="flex-1 text-center">
                          <h3 className="font-semibold text-base text-gray-900 mb-1">
                            Artworks
                          </h3>
                          <p className="text-2xl font-bold text-gray-900">
                            {stats.totalArtworks}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 bg-transparent rounded-lg p-4 border border-gray-200 flex flex-col justify-center">
                      <div className="flex flex-row sm:flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#343E4E'}}>
                          <FaUsers className="text-white text-xl" />
                        </div>
                        <div className="flex-1 text-center">
                          <h3 className="font-semibold text-base text-gray-900 mb-1">
                            Users
                          </h3>
                          <p className="text-2xl font-bold text-gray-900">
                            {stats.totalUsers}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 bg-transparent rounded-lg p-4 border border-gray-200 flex flex-col justify-center">
                      <div className="flex flex-row sm:flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#343E4E'}}>
                          <FaBuilding className="text-white text-xl" />
                        </div>
                        <div className="flex-1 text-center">
                          <h3 className="font-semibold text-base text-gray-900 mb-1">
                            Galleries
                          </h3>
                          <p className="text-2xl font-bold text-gray-900">
                            {stats.totalGalleries}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1 order-2">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Management</h2>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 min-h-[220px] flex flex-col justify-center">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div
                      onClick={() => navigate('/users')}
                      className="flex-1 bg-transparent rounded-lg p-4 hover:bg-gray-50 transition-all duration-200 cursor-pointer group font-['Montserrat'] border border-gray-200 flex flex-col justify-center"
                    >
                      <div className="flex flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200" style={{backgroundColor: '#343E4E'}}>
                          <FaUserCog className="text-white text-xl" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-base text-gray-900 mb-1">
                            Users
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => navigate('/galleries')}
                      className="flex-1 bg-transparent rounded-lg p-4 hover:bg-gray-50 transition-all duration-200 cursor-pointer group font-['Montserrat'] border border-gray-200 flex flex-col justify-center"
                    >
                      <div className="flex flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200" style={{backgroundColor: '#343E4E'}}>
                          <FaWarehouse className="text-white text-xl" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-base text-gray-900 mb-1">
                            Galleries
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => navigate('/gallery-config')}
                      className="flex-1 bg-transparent rounded-lg p-4 hover:bg-gray-50 transition-all duration-200 cursor-pointer group font-['Montserrat'] border border-gray-200 flex flex-col justify-center"
                    >
                      <div className="flex flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200" style={{backgroundColor: '#343E4E'}}>
                          <FaMicrochip className="text-white text-xl" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-base text-gray-900 mb-1">
                            Devices
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => navigate('/admin/blogs')}
                      className="flex-1 bg-transparent rounded-lg p-4 hover:bg-gray-50 transition-all duration-200 cursor-pointer group font-['Montserrat'] border border-gray-200 flex flex-col justify-center"
                    >
                      <div className="flex flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200" style={{backgroundColor: '#343E4E'}}>
                          <FaBlog className="text-white text-xl" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-base text-gray-900 mb-1">
                            Blogs
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1 order-3">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-200">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#343E4E'}}>
                        <FaNewspaper className="text-white text-lg" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Latest Updates</h3>
                        <p className="text-sm text-gray-600 mt-1">Recent platform changes and announcements</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <News />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1 order-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-200">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#343E4E'}}>
                        <FaRoad className="text-white text-lg" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Development Roadmap</h3>
                        <p className="text-sm text-gray-600 mt-1">Upcoming features and improvements</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <Roadmap />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

